import React, { useEffect, useState } from "react";
import {
    Info,
    ShieldCheck,
    Activity,
    Wifi,
    Globe,
    Clock,
    Server,
    CheckCircle2,
    XCircle,
    Hash,
    Cpu
} from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";

interface CameraDetails {
    updated: string;
    serial_number: string;
    firmware: string;
    auth_success: boolean;
    auth_method: string;
    onvif_available: boolean;
    reachable: boolean;
    dhcp: string;
    streams: string;
    dns_servers: string;
    scan_timestamp: string;
    details: string;
}

interface CameraOverviewProps {
    onDynamicDataLoaded?: (data: any) => void;
}

const CameraOverview: React.FC<CameraOverviewProps> = ({ onDynamicDataLoaded }) => {
    const [details, setDetails] = useState<CameraDetails | null>(null);
    const [loading, setLoading] = useState(true);

    const info = JSON.parse(localStorage.getItem("internal_ids") || "{}");
    const asset_id = info.asset_id;
    const client_id = info.client_id;

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const params = {
                query: JSON.stringify({ asset_id, client_id }),
            };
            const response = await baseapi.get("/asset_camera_details", { params });
            if (response.data.status && response.data.data?.length > 0) {
                const data = response.data.data[0];
                setDetails(data);

                if (data.details) {
                    try {
                        const parsed = JSON.parse(data.details);
                        if (onDynamicDataLoaded) {
                            onDynamicDataLoaded(parsed);
                        }
                    } catch (e) {
                        console.error("Failed to parse camera details", e);
                    }
                }
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-48 bg-slate-50">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#422568]"></div>
            </div>
        );
    }

    const formatDate = (dateString: string) => {
        if (!dateString || dateString === "-") return "-";
        try {
            const normalized = /[zZ]|[+-]\d{2}:\d{2}$/.test(dateString) ? dateString : `${dateString}Z`;
            const date = new Date(normalized);
            if (isNaN(date.getTime())) return dateString;
            return date.toLocaleString("en-IN", {
                year: "numeric", month: "short", day: "numeric",
                hour: "2-digit", minute: "2-digit", second: "2-digit",
                hour12: true,
            });
        } catch { return dateString; }
    };

    if (!details) {
        return (
            <div className="text-center py-10 bg-white rounded-xl border border-slate-200 shadow-sm">
                <Info className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-bold uppercase text-[11px] tracking-widest">No camera details found.</p>
            </div>
        );
    }

    const DetailCard = ({ icon: Icon, label, value, status }: { icon: any, label: string, value: any, status?: boolean }) => (
        <div className="bg-white p-2.5 rounded-lg border border-slate-200 hover:border-[#422568] transition-all group shadow-sm">
            <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-slate-50 rounded-md group-hover:bg-[#422568] transition-colors shrink-0">
                    <Icon className="w-4 h-4 text-[#422568] group-hover:text-white" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight leading-none mb-1">{label}</p>
                    {status !== undefined ? (
                        <div className="flex items-center gap-1.5">
                            {status ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <XCircle className="w-3 h-3 text-rose-600" />}
                            <span className={`text-xs font-black leading-none ${status ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {value}
                            </span>
                        </div>
                    ) : (
                        <p className="text-xs font-black text-slate-700 truncate leading-none" title={value?.toString()}>
                            {value || "N/A"}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-4 animate-fade-in">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-2">
                <DetailCard icon={Hash} label="Serial" value={details.serial_number} />
                <DetailCard icon={Cpu} label="Firmware" value={details.firmware} />
                <DetailCard icon={ShieldCheck} label="Auth" value={details.auth_success ? "Success" : "Failed"} status={details.auth_success} />
                <DetailCard icon={Activity} label="Status" value={details.reachable ? "Online" : "Offline"} status={details.reachable} />
                <DetailCard icon={Wifi} label="DHCP" value={details.dhcp === "True" ? "On" : "Off"} status={details.dhcp === "True"} />
                <DetailCard icon={Globe} label="ONVIF" value={details.onvif_available ? "Yes" : "No"} status={details.onvif_available} />
                <DetailCard icon={Server} label="Method" value={details.auth_method} />
                <DetailCard icon={Clock} label="Updated" value={formatDate(details.updated)} />
            </div>

            {details.dns_servers && JSON.parse(details.dns_servers || "[]").length > 0 && (
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm">
                    <div className="px-3 py-1.5 border-b border-white bg-slate-50/50 flex items-center gap-2">
                        <Globe className="w-3.5 h-3.5 text-[#422568]" />
                        <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-widest">DNS Configuration</h3>
                    </div>
                    <div className="p-2.5">
                        <div className="flex flex-wrap gap-1.5">
                            {JSON.parse(details.dns_servers || "[]").map((dns: string, i: number) => (
                                <span key={i} className="px-2 py-1 bg-slate-100 text-blue-700 border border-slate-200 rounded text-[10px] font-black leading-none">
                                    {dns}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CameraOverview;
